import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Ban, Loader2, Scale, Sparkles } from 'lucide-react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { FlightProviderAttempt, RebookingOption } from '@/lib/rebooking/types';
import type { RebookingAvailability } from '@/lib/rebooking/api';
import type { RosterRebookingStatus } from '@/lib/disruptions/types';
import { airlineProviderSkipped, chainForVerdict } from '@/lib/rebooking/flight-provider';
import { needsPassengerAges } from '@/lib/rebooking/passenger-ages';
import { noOptionsReasonLabel } from '@/routes/disruptions/labels';
import { PnrOptionRow } from './pnr-option-row';
import { FieldLabel } from './airport-ui';
import { ProviderChainNote } from './provider-chain-note';

// Decision provenance (POL-11): map the rebooking policy resolver's match scope to a
// human label so the operator can see WHICH policy governed these offers. Reads the
// live i18next instance rather than a hook — several surfaces call it as a plain
// function, and each of them re-renders on a language change through its own `t`.
export function policyScopeLabel(matchType?: string): string {
  switch (matchType) {
    case 'EXACT_AIRPORT':
      return i18next.t('policyScope.exactAirport', { ns: 'pnr' });
    case 'REGIONAL':
      return i18next.t('policyScope.regional', { ns: 'pnr' });
    case 'GLOBAL':
      return i18next.t('policyScope.global', { ns: 'pnr' });
    default:
      return matchType
        ? matchType.replace(/_/g, ' ').toLowerCase()
        : i18next.t('policyScope.fallback', { ns: 'pnr' });
  }
}

// The LEFT column — the ranked re-accommodation OPTIONS for this PNR (generate /
// select / reject). The assigned flight + its date + the Issue-ticket / Change
// actions live in the AssignedFlightStrip (in the hero), so they're shown ONCE,
// next to the flight info — not duplicated here. When a flight is already chosen
// this column reads as "Other options" (the alternatives to switch to). All
// actions are callbacks; no data/mutation hooks.

interface FlightResolvePanelProps {
  externalCandidates?: ReactNode;
  externalPurchaseOnly?: boolean;
  /** #771 — a CONFIRMED external (LCC) purchase exists for this group: the
   *  Generate control carries an explicit already-purchased warning. */
  externalPurchased?: boolean;
  legacyActionsAvailable?: boolean;
  /** The ranked options for this PNR (sorted by rank). */
  options: RebookingOption[];
  /** The live selected option URN, if any. */
  selectedUrn?: string | undefined;
  /** A re-accommodation IS chosen (live selectedUrn or SELECTED status). */
  isSelected: boolean;
  /** The PNR is ticketed — the flight is committed in the PSS, so switching to
   *  another option is locked (rows stay visible for reference only). */
  isTicketed?: boolean;
  /** A select mutation is in flight (disables the option rows). */
  busy: boolean;
  /** The per-PNR generate mutation is in flight. */
  generating: boolean;
  /** Generation has been queued in-session (shows the "generating…" hint). */
  hasGenerated: boolean;
  /** The options query is loading. */
  optionsLoading: boolean;
  /** The options query errored. */
  optionsError: boolean;
  /** The live rebooking status — drives the terminal no-options copy vs the
   *  not-yet-generated/PENDING generate flow. */
  liveStatus?: RosterRebookingStatus | undefined;
  /** Coded reason the group is in a terminal no-options state (NO_OPTIONS /
   *  NOT_APPLICABLE) — mapped to human copy when set. */
  noOptionsReason?: string | undefined;
  /** Governing policy URN — links the POLICY_DISABLED no-options copy to the policy. */
  governingPolicyUrn?: string | undefined;
  /**
   * The flight providers the latest search asked, in order, and why each did or did not
   * answer (contract `airline-air-ticketing.v1` §3). Said under a no-options verdict, and
   * above the options when the airline provider was passed over — «Proveedor de la
   * aerolínea: no conectado · Duffel: 2 ofertas».
   */
  providerAttempts?: readonly FlightProviderAttempt[] | undefined;
  /**
   * Pre-run policy gate (rebooking TDS §18 "opt-in by absence"): when
   * `enabled: false`, generating could only end POLICY_DISABLED — so the
   * Generate action is withheld up front and the "disabled by policy" copy
   * (with the governing-policy link) renders instead. `undefined` = gate
   * unknown/loading → today's behavior (never withhold on a blip).
   */
  policyGate?: RebookingAvailability | undefined;
  /**
   * Care-only case (`rebookingDisabled` on the case): rebooking was DECLARED off
   * when the case was opened — the engine 409s CASE_REBOOKING_DISABLED on
   * Generate. The action is withheld with its own copy (not the policy one)
   * and only the supervisor "generate anyway" (which the domain honours as a
   * policy override) remains. Options that a forced run produced still list.
   */
  careOnly?: boolean | undefined;
  /**
   * «Cargar edades» (`PassengerAgesAction`): shown under the no-options verdict when a provider
   * declined for want of a child's / infant's age (`passenger_ages_required`, or an age that
   * does not fit its type). The panel decides when; the caller builds it (it saves and
   * regenerates).
   */
  agesAction?: ReactNode;
  /** Generate options for this PNR. */
  onGenerate: () => void;
  /**
   * POL-16 — supervisor-forced generation past a disabling policy, with a
   * mandatory audited reason. Rendered (role-gated to OPS_SUPERVISOR) inside
   * the policy-gate / POLICY_DISABLED copy; absent ⇒ no force affordance.
   */
  onForceGenerate?: ((reason: string) => void) | undefined;
  /** Select a ranked option by URN. */
  onSelect: (urn: string) => void;
  /** Open the reject-all confirm. */
  onRejectAll: () => void;
}

// Beyond the top N options we collapse the rest behind a "show more" toggle so
// the column stays scannable.
const VISIBLE_OPTIONS = 3;

/**
 * POL-16 — the supervisor's "generate anyway" affordance shown inside the
 * policy-gate / POLICY_DISABLED copy. Expands in place to demand the audited
 * reason before firing; the caller wraps it in a RoleGate (OPS_SUPERVISOR).
 */
function ForceGenerateControl({
  generating,
  onConfirm,
}: {
  generating: boolean;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useTranslation('pnr');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <div className="pl-[20px] pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="drawer-force-generate"
        >
          <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('resolve.forceGenerate')}
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5 pl-[20px] pt-1" data-testid="drawer-force-generate-form">
      <p className="text-xs text-muted-foreground">{t('resolve.forceGenerateHint')}</p>
      <label className="block text-xs font-medium" htmlFor="force-generate-reason">
        {t('resolve.forceReasonLabel')}
      </label>
      <textarea
        id="force-generate-reason"
        className="w-full rounded-md border border-border bg-background p-2 text-sm"
        rows={2}
        value={reason}
        placeholder={t('resolve.forceReasonPlaceholder')}
        onChange={(e) => setReason(e.target.value)}
        data-testid="drawer-force-generate-reason"
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!reason.trim() || generating}
          onClick={() => onConfirm(reason.trim())}
          data-testid="drawer-force-generate-confirm"
        >
          {generating ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden />
          )}
          {t('resolve.forceConfirm')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false);
            setReason('');
          }}
          data-testid="drawer-force-generate-cancel"
        >
          {t('resolve.forceCancel')}
        </Button>
      </div>
    </div>
  );
}

export function FlightResolvePanel({
  externalCandidates,
  externalPurchaseOnly = false,
  externalPurchased = false,
  legacyActionsAvailable = true,
  options,
  selectedUrn,
  isSelected,
  isTicketed = false,
  busy,
  generating,
  hasGenerated,
  optionsLoading,
  optionsError,
  liveStatus,
  noOptionsReason,
  governingPolicyUrn,
  providerAttempts,
  policyGate,
  careOnly,
  agesAction,
  onGenerate,
  onForceGenerate,
  onSelect,
  onRejectAll,
}: FlightResolvePanelProps) {
  const { t } = useTranslation('pnr');
  const [showAll, setShowAll] = useState(false);
  // The providers could not quote for want of an age: the way out is the operator's.
  const askAges = needsPassengerAges(chainForVerdict(providerAttempts, noOptionsReason)) ? agesAction : null;

  if (externalPurchaseOnly || !legacyActionsAvailable) {
    return <section data-testid="drawer-options">{externalCandidates}</section>;
  }

  // NEX-737: hide the entire flight options panel when a ticket is issued —
  // the flight is committed in the PSS and cannot be changed. The re-accommodation
  // diagram already tells the story; showing locked options adds no value.
  if (isTicketed) {
    return null;
  }

  const hasOptions = options.length > 0;
  // Terminal no-options state (rebooking ran + ruled everything out, or no policy
  // applies) WITH a coded reason → show the human "why" instead of the generic
  // "generate options" empty state. The Generate button stays only for the
  // not-yet-generated / PENDING case.
  const terminalNoOptions =
    (liveStatus === 'NO_OPTIONS' || liveStatus === 'NOT_APPLICABLE') &&
    !!noOptionsReason &&
    !!noOptionsReasonLabel(noOptionsReason, t);
  const reasonCopy = terminalNoOptions ? (noOptionsReasonLabel(noOptionsReason, t) ?? '') : '';
  const visible = showAll ? options : options.slice(0, VISIBLE_OPTIONS);
  const hiddenCount = options.length - visible.length;
  // Provenance (POL-11): all of a group's options share the governing policy — show it
  // once so the operator sees WHY / under which policy these flights were offered.
  const governing = options.find((o) => o.policyMatchType || o.governingPolicyUrn);

  return (
    <section data-testid="drawer-options" className="rounded-lg border border-border p-3">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <FieldLabel className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {isSelected ? t('resolve.otherOptions') : t('resolve.title')}
        </FieldLabel>
        {hasOptions && !isSelected ? (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={onRejectAll}
            data-testid="drawer-reject-all"
          >
            <Ban className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('resolve.rejectAll')}
          </Button>
        ) : null}
      </div>

      {isTicketed ? (
        <p
          className="mb-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground"
          data-testid="drawer-ticket-locked"
        >
          {t('resolve.ticketLocked')}
        </p>
      ) : null}

      {hasOptions && governing ? (
        <p
          className="mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground"
          data-testid="drawer-policy-provenance"
          {...(governing.governingPolicyUrn ? { title: governing.governingPolicyUrn } : {})}
        >
          <Scale className="h-3 w-3 shrink-0" aria-hidden />
          {t('resolve.offeredUnder', { scope: policyScopeLabel(governing.policyMatchType) })}
        </p>
      ) : null}

      {hasOptions && airlineProviderSkipped(providerAttempts) ? (
        // Another provider sold these seats: say why the airline provider did not.
        <ProviderChainNote attempts={providerAttempts} className="mb-2" testId="drawer-provider-chain" />
      ) : null}

      {!hasOptions ? (
        optionsLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : optionsError ? (
          <p className="text-sm text-destructive">{t('resolve.loadError')}</p>
        ) : careOnly ? (
          // Care-only case: rebooking was declared off at case creation — not a
          // policy verdict, so neither the policy copy nor its link apply. The
          // hotel / care flow continues; a supervisor may still force a run.
          <div
            className="space-y-1 rounded-md border border-dashed border-border p-3 text-sm"
            data-testid="drawer-care-only-gate"
          >
            <p className="flex items-start gap-1.5 text-muted-foreground">
              <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{t('resolve.careOnlyGate')}</span>
            </p>
            {onForceGenerate ? (
              <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
                <ForceGenerateControl generating={generating} onConfirm={onForceGenerate} />
              </RoleGate>
            ) : null}
          </div>
        ) : terminalNoOptions ? (
          // Rebooking ran + reached a terminal no-options verdict — explain WHY
          // instead of inviting the operator to generate (there's nothing to generate).
          // POLICY_DISABLED (the primary case) links to the governing policy.
          <div
            className="space-y-1 rounded-md border border-dashed border-border p-3 text-sm"
            data-testid="drawer-no-options-reason"
          >
            <p className="flex items-start gap-1.5 text-muted-foreground">
              <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{reasonCopy}</span>
            </p>
            <ProviderChainNote
              attempts={chainForVerdict(providerAttempts, noOptionsReason)}
              testId="drawer-provider-chain"
            />
            {askAges ? <div className="pl-[20px] pt-1">{askAges}</div> : null}
            {noOptionsReason === 'POLICY_DISABLED' && governingPolicyUrn ? (
              <Link
                to={`/policies/${encodeURIComponent(governingPolicyUrn)}`}
                className="inline-block pl-[20px] text-xs text-primary hover:underline"
                data-testid="drawer-no-options-policy-link"
              >
                {t('policy.view')}
              </Link>
            ) : null}
            {noOptionsReason === 'POLICY_DISABLED' && onForceGenerate ? (
              <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
                <ForceGenerateControl generating={generating} onConfirm={onForceGenerate} />
              </RoleGate>
            ) : null}
          </div>
        ) : policyGate?.enabled === false ? (
          // Pre-run policy gate: the governing policy for this flight's station
          // carries no rebooking block — generating could only end
          // POLICY_DISABLED, so the action is withheld up front. The hotel /
          // care flow continues; only rebooking is off the table.
          <div
            className="space-y-1 rounded-md border border-dashed border-border p-3 text-sm"
            data-testid="drawer-policy-gate"
          >
            <p className="flex items-start gap-1.5 text-muted-foreground">
              <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{t('resolve.policyGate')}</span>
            </p>
            {policyGate.policyUrn ? (
              <Link
                to={`/policies/${encodeURIComponent(policyGate.policyUrn)}`}
                className="inline-block pl-[20px] text-xs text-primary hover:underline"
                data-testid="drawer-policy-gate-link"
              >
                {t('policy.view')}
              </Link>
            ) : null}
            {onForceGenerate ? (
              <RoleGate min={Role.OPS_SUPERVISOR} hideWhenDenied>
                <ForceGenerateControl generating={generating} onConfirm={onForceGenerate} />
              </RoleGate>
            ) : null}
          </div>
        ) : isTicketed ? null : ( // ticketed ⇒ never offer Generate — the engine 409s (ALREADY_TICKETED) and the lock note above says why
          <div
            className="space-y-2 rounded-md border border-dashed border-border p-3 text-sm"
            data-testid="drawer-no-options"
          >
            <p className="text-muted-foreground">{t('resolve.noOptions')}</p>
            <ProviderChainNote
              attempts={chainForVerdict(providerAttempts, noOptionsReason)}
              testId="drawer-provider-chain"
            />
            {askAges}
            {externalPurchased ? (
              <p className="text-xs text-amber-600" data-testid="drawer-external-purchased-warning">
                {t('resolve.externalPurchasedWarning')}
              </p>
            ) : null}
            <Button
              size="sm"
              onClick={onGenerate}
              disabled={generating}
              data-testid="drawer-generate-options"
            >
              {generating ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t('resolve.generate')}
            </Button>
            {hasGenerated ? (
              <p className="text-xs text-muted-foreground" data-testid="drawer-generate-queued">
                {t('resolve.generating')}
              </p>
            ) : null}
          </div>
        )
      ) : (
        <ul
          className="divide-y divide-border overflow-hidden rounded-md border border-border"
          data-testid="drawer-option-list"
        >
          {visible.map((option, idx) => (
            <PnrOptionRow
              key={option.urn}
              option={option}
              recommended={idx === 0 && !isSelected}
              selected={option.urn === selectedUrn}
              pending={busy}
              locked={isTicketed}
              onSelect={() => onSelect(option.urn)}
            />
          ))}
          {hiddenCount > 0 ? (
            <li>
              <Button
                size="sm"
                variant="ghost"
                className="w-full rounded-none text-muted-foreground"
                onClick={() => setShowAll(true)}
                data-testid="drawer-show-more-options"
              >
                {t('resolve.showMore', { count: hiddenCount })}
              </Button>
            </li>
          ) : null}
        </ul>
      )}
      {externalCandidates ? <div className="mt-4">{externalCandidates}</div> : null}
    </section>
  );
}
